import React, { ChangeEvent, useState, useRef } from 'react';
import styled from 'styled-components';
import { FormControl, Select } from '@uhg-harmony/uitk-react';
import {
  StyledClose,
  StyledPickerHeader,
  StyledPickerPopover,
  StyledPickerWrapper,
} from '../GradientColorPicker/GradientColorPicker';
import { ColorButton } from '../ColorButton/ColorButton';
import { MainLabelStyles, handleNumberInput } from './Typography/Typography';
import { ColorButtonWrapper } from './Background/Background';
import GradientColorPicker from '../GradientColorPicker/GradientColorPicker';
import { toast } from 'react-toastify';
import BorderVisibilityPicker from '../BorderVisibilityPicker/BorderVisibilityPicker';
import { defaultTheme as theme } from 'shared/theme/theme';

const BorderThicknessSelect = styled(Select)`
  font-family: OptumSans, sans-serif;
  width: 4rem;
  padding: 0 0.5rem 0 0;
  border-radius: 0.375rem;
  & > select {
    font-family: OptumSans, sans-serif;
    line-height: 1.25rem;
  }
`;

const BorderRadiusInputWrapper = styled.div`
  display: block;
`;

const BorderRadiusInput = styled.input`
  width: 4.5rem;
  height: 2.3rem;
  border-radius: 5px;
  font-size: 1rem;
  padding-left: 0.75rem;
  margin-top: 0.315rem;
  border: 1px solid #4b4d4f;
  color: #4b4d4f;
  &:disabled {
    background-color: #f3f3f3;
    border-color: #929496;
    color: #929496;
  }
`;

const SpaceDiv = styled.div`
  display: flex;
  flex-wrap: nowrap;
  padding-right: 0.5rem;
  align-items: center;
`;

const BorderDropdowns = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
`;

const BorderWidthWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const BorderWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  padding: 1rem 0;
`;

const BorderVisibilityPickerWrapper = styled.div``;

export interface BorderProps<T> {
  updateBorderData: (data: BorderData) => void;
  currentTab?: number;
  disabled?: boolean;
  handleLineColor?: (data: string) => void;
  handleBackgroundColor?: (data: string) => void;
  widgetState: Widget<T>;
  pages?: Array<Page>;
  site?: Site;
  updateSite?: (site: Site) => void;
  saveSite?: () => void;
}
const borderThicknessSizes = Array.from(Array(11), (_, index) => index);

export function getThickness(widgetState, tab) {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.borderData?.thickness;
    case 1:
      return widgetState.config.hoverData?.borderData?.thickness;
    default:
  }
}

export function getRadius(widgetState, tab) {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.borderData?.radius;
    case 1:
      return widgetState.config.hoverData?.borderData?.radius;
    default:
  }
}

export function getLineColor(widgetState, tab) {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.lineColor;
    case 1:
      return widgetState.config.hoverData?.lineColor;
    default:
  }
}

export function getBorderData(widgetState, tab) {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.borderData;
    case 1:
      return widgetState.config.hoverData?.borderData;
    default:
  }
}

export const getTopBorderData = (widgetState, tab) => {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.borderData?.isTopBorderVisible;
    case 1:
      return widgetState.config.hoverData.borderData?.isTopBorderVisible;
  }
};

export const getBottomBorderData = (widgetState, tab) => {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.borderData?.isBottomBorderVisible;
    case 1:
      return widgetState.config.hoverData.borderData?.isBottomBorderVisible;
  }
};

export const getLeftBorderData = (widgetState, tab) => {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.borderData?.isLeftBorderVisible;
    case 1:
      return widgetState.config.hoverData.borderData?.isLeftBorderVisible;
  }
};

export const getRightBorderData = (widgetState, tab) => {
  switch (tab) {
    case undefined:
    case 0:
      return widgetState.config.borderData?.isRightBorderVisible;
    case 1:
      return widgetState.config.hoverData.borderData?.isRightBorderVisible;
  }
};

export const Border: React.FC<BorderProps<any>> = (props: BorderProps<any>) => {
  const {
    widgetState,
    updateBorderData,
    handleLineColor,
    handleBackgroundColor,
    site,
    updateSite,
    saveSite,
    currentTab,
    disabled,
  } = props;

  const [border, setBorder] = useState(getBorderData(widgetState, currentTab));
  const [thickness, setThickness] = useState(
    getThickness(widgetState, currentTab)
  );
  const [radius, setRadius] = useState(getRadius(widgetState, currentTab));
  const [color, setColor] = useState(getLineColor(widgetState, currentTab));
  const [pickerOpen, setPickerOpen] = useState(false);
  const colorButtonRef = useRef<HTMLDivElement | null>(null);
  const pickerId = pickerOpen ? 'simple-popover' : undefined;

  React.useEffect(() => {
    updateBorderData(border);
  }, [border]);

  React.useEffect(() => {
    handleLineColor(color);
  }, [color]);

  React.useEffect(() => {
    setColor(getLineColor(widgetState, currentTab));
    setThickness(getThickness(widgetState, currentTab));
    setRadius(getRadius(widgetState, currentTab));
  }, [widgetState.config]);

  const handleThicknessChange = (e: ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    setThickness(e.target.value);
    setBorder({
      ...border,
      thickness: parseInt(e.target.value),
      isTopBorderVisible: getTopBorderData(widgetState, currentTab),
      isBottomBorderVisible: getBottomBorderData(widgetState, currentTab),
      isLeftBorderVisible: getLeftBorderData(widgetState, currentTab),
      isRightBorderVisible: getRightBorderData(widgetState, currentTab),
    });
    updateBorderData({
      ...border,
      thickness: parseInt(e.target.value),
      isTopBorderVisible: getTopBorderData(widgetState, currentTab),
      isBottomBorderVisible: getBottomBorderData(widgetState, currentTab),
      isLeftBorderVisible: getLeftBorderData(widgetState, currentTab),
      isRightBorderVisible: getRightBorderData(widgetState, currentTab),
    });
  };

  const handleRadiusChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setRadius(e.target.value);
    setBorder({
      ...border,
      radius: parseInt(e.target.value),
      isTopBorderVisible: getTopBorderData(widgetState, currentTab),
      isBottomBorderVisible: getBottomBorderData(widgetState, currentTab),
      isLeftBorderVisible: getLeftBorderData(widgetState, currentTab),
      isRightBorderVisible: getRightBorderData(widgetState, currentTab),
    });
    updateBorderData({
      ...border,
      radius: parseInt(e.target.value),
      isTopBorderVisible: getTopBorderData(widgetState, currentTab),
      isBottomBorderVisible: getBottomBorderData(widgetState, currentTab),
      isLeftBorderVisible: getLeftBorderData(widgetState, currentTab),
      isRightBorderVisible: getRightBorderData(widgetState, currentTab),
    });
  };

  const handleColorChange = newColor => {
    if (colorButtonRef.current?.id === 'line-color') {
      if (newColor.startsWith('linear') || newColor.startsWith('radial')) {
        toast('Gradient cannot be used for border color', {
          type: 'warning',
          theme: 'colored',
        });
      } else {
        setColor(newColor);
        handleLineColor(newColor);
      }
    }
    if (colorButtonRef.current?.id === 'background-color') {
      setColor(newColor);
      handleBackgroundColor(newColor);
    }
  };

  const handleColorEditClick = (e: React.MouseEvent<any>) => {
    if (disabled) return;
    e.stopPropagation();
    e.preventDefault();
    colorButtonRef.current = e.currentTarget as HTMLDivElement;
    setPickerOpen(true);
  };

  const handlePickerClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handlePickerClose = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    setPickerOpen(false);
    colorButtonRef.current = null;
  };

  return (
    <BorderWrapper>
      <BorderVisibilityPickerWrapper>
        <BorderVisibilityPicker
          updateBorderData={updateBorderData}
          widgetState={widgetState}
          getBottomBorderData={getBottomBorderData}
          getLeftBorderData={getLeftBorderData}
          getRightBorderData={getRightBorderData}
          getTopBorderData={getTopBorderData}
        />
      </BorderVisibilityPickerWrapper>
      <BorderDropdowns>
        <SpaceDiv>
          <MainLabelStyles>Color</MainLabelStyles>
          <ColorButtonWrapper
            id="line-color"
            data-test-id="line-color"
            onClick={handleColorEditClick}
            ref={colorButtonRef}
          >
            <ColorButton
              disabled={disabled}
              color={disabled ? theme.colors.aux.lighterGrey : color}
            />
          </ColorButtonWrapper>
        </SpaceDiv>
        <StyledPickerPopover
          data-test-id="widget-color-picker-popover"
          id={pickerId}
          open={pickerOpen}
          onClick={handlePickerClick}
          onClose={handlePickerClose}
          anchorEl={colorButtonRef.current}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
        >
          <StyledPickerWrapper>
            <StyledPickerHeader data-test-id="styled-picker-header">
              Color Selector
            </StyledPickerHeader>
            <StyledClose
              data-test-id="close-icon"
              onClick={handlePickerClose}
            />
          </StyledPickerWrapper>
          <GradientColorPicker
            value={getLineColor(widgetState, currentTab)}
            onChange={handleColorChange}
            site={site}
            handleColorChange={handleColorChange}
            updateSite={updateSite}
            saveSite={saveSite}
            anchorEl={colorButtonRef.current}
          />
        </StyledPickerPopover>
        <BorderWidthWrapper>
          <MainLabelStyles>Width</MainLabelStyles>
          <FormControl
            data-test-id="border-thickness-dropdown"
            id={'controlled-select'}
          >
            <BorderThicknessSelect
              onChange={handleThicknessChange}
              value={thickness}
              name="thickness"
              data-test-id="border-thickness"
              style={{ fontFamily: 'OptumSans' }}
            >
              {borderThicknessSizes.map(size => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </BorderThicknessSelect>
          </FormControl>
        </BorderWidthWrapper>
        <BorderRadiusInputWrapper>
          <MainLabelStyles>Radius</MainLabelStyles>
          <BorderRadiusInput
            disabled={disabled}
            id="border-radius"
            value={radius}
            onChange={handleRadiusChange}
            onKeyDown={handleNumberInput}
            data-test-id="border-radius"
            type="number"
            min={0}
            style={{ fontFamily: 'OptumSans' }}
          />
        </BorderRadiusInputWrapper>
      </BorderDropdowns>
    </BorderWrapper>
  );
};
